<template>
	<div
		class="teaser-small"
		ref="teaserElement"
		:class="{ 'is-web-teaser': !isNativeDevice }"
		@mouseenter="!isNativeDevice ? handleMouseEnter() : null"
		@mouseleave="!isNativeDevice ? handleMouseLeave() : null"
		@focus="isNativeDevice ? showReplica() : null"
		@click="isNativeDevice ? navigateToInnerPage() : null"
		v-focus
	>
		<img
			ref="imageRef"
			@click="!isNativeDevice ? play() : null"
			:src="currentSrc"
			:alt="item?.field_image?.meta?.alt"
			:class="{ 'lazy-loaded': isLoaded }"
		/>
		<div
			v-if="timeline !== undefined && isContinueWatching"
			class="video-timeline-progress"
			:style="{
				background: `linear-gradient(to right, #f13c36 0%, #f13c36 ${timeline}%, #ffffff ${timeline}%, #ffffff 100%)`,
			}"
		></div>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted, watch, Ref } from "vue"
	import { useIdle, usePointer } from "@vueuse/core"
	import { Movie } from "../../types/Movie"
	import { Series } from "../../types/Series"
	import { Episode } from "../../types/Episode"
	import { useTeaserPreview } from "../../composables/useTeaserPreview"
	import { useLazyImage } from "../../composables/useLazyImage"
	import { useTeaserStore } from "../../stores/teaser"
	import { useDeviceManager } from "../../stores/deviceManager"
	import { useRouter } from "vue-router"
	import { extractDurationFromKwik } from "../../utils/media"
	import { useTimestampStore } from "../../stores/timestamps"
	import { useUserProfiles } from "../../composables/useUserProfile"
	import api from "../../composables/useApi"
	import { url } from "../../helper"
	import { usePlay } from "../../composables/usePlay"
	const { playElement, selectedEpisodeNid } = usePlay()
	const { fetchVideoFlagStoreByProfile } = useUserProfiles()
	const router = useRouter()
	const { pointerType } = usePointer()
	const deviceManager = useDeviceManager()
	const props = defineProps<{
		item: Movie | Series
		isFirstVisibleElement?: boolean
		isLastVisibleElement?: boolean
		isContinueWatching?: boolean
	}>()
	const timeline: Ref<number | undefined> = ref(undefined)

	const isNativeDevice = computed(() => deviceManager.deviceInfo.platform !== "web" || pointerType.value === "touch") //&& pointerType.value === "touch"

	const emit = defineEmits(["preview-ended"])
	const teaserStore = useTeaserStore()
	const teaserElement = ref<HTMLElement | null>(null)
	const { replicateElement } = useTeaserPreview()
	const { imageRef, currentSrc, isLoaded, observe } = useLazyImage()
	const isHovering = ref(false)
	import { useI18n } from "vue-i18n"
	import { Capacitor } from "@capacitor/core"
	const { locale } = useI18n()
	const nextEpisodeId = ref<string | null>(null)
	const time: Ref<number | undefined> = ref(undefined)

	// useIdle tracks if the user has been inactive for 500ms
	const { idle } = useIdle(300)

	const imageSrc = computed(
		() => props.item?.field_image?.image_style_uri?.teaser_small || props.item?.teaser_small_image_url || ""
	)
	const timestampStore = useTimestampStore()
	const entityType = ref("")
	const duration = ref(0)
	const episodeInfo = ref("")
	const selectedEpisodeNid1 = ref<number>(0)
	const imageUrl = computed(() => {
		return props.item?.field_image?.image_style_uri?.teaser_large || undefined
	})

	onMounted(async () => {
		if (imageSrc.value) {
			observe(imageSrc.value)
		}
		if (props.isContinueWatching) {
			const entityId = props.item?.type.includes("movie") ? (props.item as Movie)?.id : undefined
			const parentId = props.item?.type.includes("series") ? (props.item as Series).id : undefined
			const flag: Ref<string | undefined> = ref("")
			if (props.item?.type.includes("movie")) {
				entityType.value = "movie"
				flag.value = "started"
			} else if (props.item?.type.includes("series")) {
				entityType.value = "episode"
				flag.value = undefined
			}
			const res = await fetchVideoFlagStoreByProfile(entityId, entityType.value, flag.value, parentId)
			if (res[0]?.video_flag_type === "completed") {
				let episodes: string[] = []
				try {
					const res = await api.get(`/jsonapi/node/episode`, {
						params: {
							"filter[field_series.id]": parentId,
							"fields[node--episode]": "drupal_internal__nid",
						},
					})
					episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
				} catch (error) {
					console.error("Failed to fetch episodes:", error)
					return
				}
				const currentId = res[0]?.target_entity.drupal_internal__nid
				const currentIndex = episodes.indexOf(currentId)
				if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
					nextEpisodeId.value = episodes[currentIndex + 1]
					const response = await api.get(
						`${locale.value}/jsonapi/node/episode?jsonapi_include=1&include=field_trailer_file,field_image,field_series,field_series.field_image,field_subtitles,field_subtitles.field_subtitle_file`,
						{
							params: {
								"filter[drupal_internal__nid]": nextEpisodeId.value,
								"filter[langcode]": locale.value,
								"filter[status]": 1,
							},
						}
					)
					selectedEpisodeNid1.value = response.data.data[0].drupal_internal__nid
					if (response.data.data[0]) {
						episodeInfo.value = `- S:${response.data.data[0].field_season} E:${response.data.data[0].title}`
					}
				}
			} else if (res.length === 0) {
				time.value = 0
				await api
					.get(`${url}/jsonapi/node/episode`, {
						params: {
							"filter[field_series.id]": parentId,
							page: {
								limit: 1,
							},
						},
					})
					.then((response) => {
						if (response.data.data && response.data.data.length > 0) {
							const episodeData = response.data.data[0]
							// duration.value = extractDurationFromKwik((episodeData as Episode)?.field_kwik_data) ?? 0
							selectedEpisodeNid1.value = episodeData.attributes.drupal_internal__nid
						}
					})
			} else {
				duration.value = extractDurationFromKwik((props.item as any)?.field_kwik_data) ?? 0
				if (props.item?.type.includes("movie")) {
					timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, props.item.drupal_internal__nid)
				}
				if (res[0]?.timestamp) {
					time.value = res[0]?.timestamp
					timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, props.item.drupal_internal__nid)

					duration.value = extractDurationFromKwik((res[0]?.target_entity as Episode)?.field_kwik_data) ?? 0
					time.value = res[0]?.timestamp
					// console.log("----time value", time.value, "----duration value", duration.value)
					// episodeInfo.value = ""
					if (res[0]?.target_entity_type === "episode") {
						// timestampStore.addTimestamp(res[0]?.timestamp, "node--episode", res[0]?.target_entity.drupal_internal__nid)
						episodeInfo.value = `- S:${res[0]?.target_entity?.field_season} E:${res[0]?.target_entity?.title}`

						selectedEpisodeNid1.value = res[0]?.target_entity?.drupal_internal__nid
					}
				}
				if (duration.value > 0 && time.value > 0) {
					timeline.value = (time.value / duration.value) * 100
				}
			}
		}
	})

	// Watch for when user becomes idle while hovering
	watch(idle, (isIdle) => {
		if (isIdle && isHovering.value && !teaserStore.state.isPreviewing) {
			triggerPreview()
		}
	})

	const triggerPreview = () => {
		if (teaserStore.state.isPreviewing) {
			return
		}

		if (props.isFirstVisibleElement) {
			teaserStore.state.isFirstVisibleElement = true
		}
		if (props.isLastVisibleElement) {
			teaserStore.state.isLastVisibleElement = true
		}
		teaserStore.setSelectedItem(props.item, "teaser_small")
		teaserStore.setTeaserPosition(teaserElement.value)
		teaserStore.handleMouseOver()
	}

	const handleMouseEnter = () => {
		isHovering.value = true
	}

	const handleMouseLeave = () => {
		isHovering.value = false
	}

	const showReplica = () => {
		teaserStore.setSelectedItem(props.item, "teaser_small")
	}

	const navigateToInnerPage = () => {
		router.push({
			name: props.item.type.includes("movie") ? "InnerMovie" : "InnerSeries",
			params: {
				id: props.item.drupal_internal__nid,
			},
		})
	}

	const isMobileDevice = computed(
		() =>
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
		// () => deviceManager.deviceInfo.platform !== "web" || window.innerWidth <= 480
	)

	const play = async () => {
		if (isMobileDevice.value === true) {
			if (props.item?.type?.includes("movie")) {
				router.push({ name: "InnerMovie", params: { id: props.item.drupal_internal__nid } })
			} else if (props.item.type?.includes("series")) {
				router.push({ name: "InnerSeries", params: { id: props.item.drupal_internal__nid } })
			}
		} else {
			await playElement(props.item)
			await teaserStore.play(
				props.item?.type.includes("movie") ? "movie" : "episode",
				props.item!,
				props.item?.type.includes("movie") ? undefined : selectedEpisodeNid.value,
				router
			)
		}
	}
</script>

<style scoped lang="scss">
	.teaser-small {
		position: relative;
		overflow: hidden;
		border-radius: 10px;
		&:focus,
		&:hover {
			cursor: pointer;
		}
	}
	img {
		width: 100%;
		display: block;
		height: auto;
		transition: opacity 0.3s ease;
		opacity: 0.5;

		&.lazy-loaded {
			opacity: 1;
		}
	}
	.video-timeline-progress {
		height: 3px;
		position: absolute;
		bottom: 0px;
		left: 0;
		right: 0;
	}
</style>
